import React, { useEffect, useRef, useState } from 'react';
import academy from '~/data/menu/menu_academy.json';
import { GRID_BREAKPOINTS } from '~/utils/constants';
import { graphql } from 'gatsby';
import useStickyScroll from '~/hooks/sticky-scroll';
import ContentProgressBar from '../../fragments/content-progress-bar';

const academyPathPrefix = '/academy';

interface AcademyMenuProps {
  block: AcademyMenuBlock;
  category: string;
}

type AcademyMenuBlock = {
  _template: string;
  css_class_name: string;
  categories: CategoryItem[];
};

type CategoryItem = {
  category: string;
  menu: MenuItem[];
};

type MenuItem = {
  text: string;
  link?: string;
  submenu?: SubmenuItem[];
};

type SubmenuItem = {
  text: string;
  link: string;
};

const AcademyMenu: React.FC<AcademyMenuProps> = ({ block, category }) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { slideDown, scrollPercent } = useStickyScroll(sectionRef);
  const stateClass = slideDown ? 'slide-down' : '';
  const academyMenu =
    academy.categories.find(menuItem => menuItem.category === category) ||
    academy.categories[0];

  if (!academyMenu) {
    return null;
  }

  return (
    <section
      className={`block ${block._template} ${block.css_class_name} ${stateClass}`}
      ref={sectionRef}
    >
      <Menu
        menu={academyMenu.menu}
        category={academyMenu.category}
      />
      <ContentProgressBar scrollPercent={scrollPercent} />
    </section>
  );
};

const Menu: React.FC<{ menu: MenuItem[]; category: string }> = ({
  menu,
  category,
}) => {
  if (!menu) {
    return null;
  }

  const isMobileMenu = () =>
    typeof window !== 'undefined' && window.innerWidth <= GRID_BREAKPOINTS.md;

  const outsideClickHandler = ({ target }: any) => {
    const isClickedOutside =
      menuRef.current && !menuRef.current.contains(target);

    if (isMobileMenu() && isOpened && isClickedOutside) {
      setIsOpened(false);
    }
  };

  const titleClickHandler = () => {
    setIsOpened(!isOpened);
  };

  const findActiveMenuItem = () => {
    const linkMatchesUrlPath = (subMenuItem: SubmenuItem) =>
      subMenuItem.link === articlePath;

    // check menu items
    let activeMenuItem: MenuItem | undefined;
    for (let i = 0; i < menu.length; i++) {
      const submenu = menu[i]?.submenu;
      if (submenu) {
        const item = submenu ? submenu.some(linkMatchesUrlPath) : null;

        if (item) {
          activeMenuItem = menu[i];
          // escape the loop
          i = menu.length;
        }
      }
    }

    return activeMenuItem ? activeMenuItem : menu[0];
  };

  const getMenuActiveClass = (isActiveCategory: boolean) =>
    isActiveCategory ? ACTIVE_ITEM_CLASS : '';

  const ACTIVE_ITEM_CLASS = ' academy-menu__item--active';
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpened, setIsOpened] = useState(false);
  const articlePath = category[1];
  const activeMenuItem = findActiveMenuItem();

  if (!activeMenuItem) {
    return null;
  }

  useEffect(() => {
    document.addEventListener('mousedown', outsideClickHandler);
    /**
     * Cleanup listeners of this hook
     */
    return () => {
      document.removeEventListener('mousedown', outsideClickHandler);
    };
  }, [menuRef, isOpened]);

  return (
    <div
      className='container--academy-menu'
      ref={menuRef}
    >
      <div
        className='academy-menu__toggle'
        onClick={titleClickHandler}
      >
        <p className='academy-menu__text'>{activeMenuItem.text}</p>
      </div>
      <ul
        className={`academy-menu ${
          isOpened ? 'academy-menu--opened' : 'academy-menu--closed'
        }`}
      >
        {menu.map((item, index) => (
          <li
            className={`academy-menu__item${getMenuActiveClass(
              activeMenuItem.text === item.text
            )}`}
            key={`academy-menu-item-${index}`}
          >
            {item.link ? (
              <a
                className='academy-menu__text academy-menu__cta'
                href={
                  item.link.includes(`/${category}/`)
                    ? item.link
                    : `${academyPathPrefix}/${category}/${item.link}`
                }
              >
                {item.text}
              </a>
            ) : (
              <p className='academy-menu__text'>{item.text}</p>
            )}
            {item.submenu && item.submenu.length > 0 && (
              <Submenu
                submenu={item.submenu}
                category={category}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Submenu: React.FC<{ submenu: SubmenuItem[]; category: string }> = ({
  submenu,
  category,
}) => {
  return (
    <div className={`academy-submenu`}>
      {submenu.map((item, index) => (
        <div
          className='academy-submenu__item'
          key={`academy-submenu-item-${index}`}
        >
          <a
            className='academy-submenu__cta'
            href={`${academyPathPrefix}/${category}/${item.link}`}
          >
            {item.text}
          </a>
        </div>
      ))}
    </div>
  );
};

export default AcademyMenu;

export const academyMenuQuery = graphql`
  fragment AcademyMenuFragment on Block {
    settings {
      margin_bottom
      css_class_name
      custom_css
    }
  }
`;
